import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Mic, 
  Eye, 
  Award, 
  BookOpen, 
  Users, 
  Play, 
  Star,
  CheckCircle,
  ArrowRight,
  Mail,
  Linkedin,
  Youtube,
  ExternalLink
} from 'lucide-react';
import { mockData } from '../data/mock';
// GuestApplicationForm removed - using direct booking link instead

export const PodcastLandingPage = () => {
  const openBookingPage = () => {
    const bookingUrl = process.env.REACT_APP_BOOKING_URL || 'https://calendar.app.google/Xgfboy7o2hyn9yww9';
    window.open(bookingUrl, '_blank');
  };

  // Removed openTestimonials - using Shapo widget instead

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Navigation - Mobile and Desktop */}
          <nav className="flex justify-center md:justify-end items-center space-x-8 mb-6">
            <a href="#about" className="text-gray-700 hover:text-purple-600 transition-colors text-sm md:text-base">About</a>
            <a href="#episodes" className="text-gray-700 hover:text-purple-600 transition-colors text-sm md:text-base">Episodes</a>
            <a href="#testimonials" className="text-gray-700 hover:text-purple-600 transition-colors text-sm md:text-base">Testimonials</a>
            <Button 
              onClick={openBookingPage}
              size="sm"
              className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
            >
              Be a Guest
            </Button>
          </nav>
          
          {/* Centered Banner */}
          <div className="flex justify-center mt-4">
            <div className="max-w-4xl w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_speak-on-nifty/artifacts/zq21twp8_Nifty%20Travel%20Podcast%20Header.png" 
                alt="Nifty Travel Podcast - Travel With Us"
                className="w-full h-auto rounded-lg shadow-sm"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-6 bg-gradient-to-r from-purple-100 to-teal-100 text-purple-800 border-purple-200">
              Join Our Growing Community of Travel Voices
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {mockData.hero.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {mockData.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg"
                onClick={openBookingPage}
                className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                <Mic className="mr-2 h-5 w-5" />
                {mockData.hero.ctaText}
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">{mockData.podcast.stats.episodes}</div>
                <div className="text-gray-600">Episodes Released</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-600 mb-2">{mockData.podcast.stats.downloads}</div>
                <div className="text-gray-600">Total Downloads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">{mockData.podcast.stats.countries}</div>
                <div className="text-gray-600">Countries Reached</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Join as a Guest?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Become part of our community and share your unique travel expertise with thousands of engaged listeners worldwide.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.benefits.map((benefit, index) => {
              const IconComponent = {
                eye: Eye,
                award: Award,
                'book-open': BookOpen,
                users: Users
              }[benefit.icon];

              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-full flex items-center justify-center">
                      <IconComponent className="text-white w-8 h-8" />
                    </div>
                    <CardTitle className="text-xl text-purple-700">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Podcast Section */}
      <section id="about" className="py-20 bg-gradient-to-br from-purple-50 to-teal-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">About the Nifty Travel Podcast</h2>
            <p className="text-lg text-gray-700 mb-12 leading-relaxed">
              {mockData.podcast.description}
            </p>
            
            {/* Featured Episodes */}
            <div id="episodes" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
              {mockData.featuredEpisodes.map((episode, index) => (
                <Card key={episode.id} className="text-left hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => window.open(episode.youtubeUrl, '_blank')}>
                  <CardHeader>
                    <div className="flex items-center space-x-2 mb-2">
                      <Play className="text-purple-600 w-5 h-5" />
                      <Badge variant="secondary">{episode.duration}</Badge>
                    </div>
                    <CardTitle className="text-lg hover:text-purple-600 transition-colors">{episode.title}</CardTitle>
                    <CardDescription className="text-teal-600">with {episode.guest}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{episode.description}</p>
                    <div className="flex items-center text-sm text-purple-600 hover:text-purple-700">
                      <Youtube className="w-4 h-4 mr-1" />
                      Watch on YouTube
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Host Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Your Host</h2>
                <h3 className="text-2xl font-semibold text-purple-700 mb-4">{mockData.host.name}</h3>
                <p className="text-lg text-teal-600 mb-6">{mockData.host.title}</p>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {mockData.host.bio}
                </p>
                <div className="flex items-center justify-center lg:justify-start space-x-4">
                  <Badge className="bg-purple-100 text-purple-800">
                    {mockData.host.experience}
                  </Badge>
                </div>
              </div>
              <div className="text-center">
                <div className="w-64 h-64 mx-auto rounded-full overflow-hidden shadow-lg border-4 border-purple-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_speak-on-nifty/artifacts/suwhf1hv_IMG_1580%20Portrait_1MB.jpeg" 
                    alt="Podcast Host Portrait"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-purple-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What to Expect as a Guest</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our simple, professional process makes it easy and enjoyable to share your story.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {mockData.process.map((step, index) => (
                <div key={step.step} className="text-center">
                  <div className="relative mb-6">
                    <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-600 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {step.step}
                    </div>
                    {index < mockData.process.length - 1 && (
                      <ArrowRight className="absolute top-1/2 -right-4 transform -translate-y-1/2 text-gray-300 w-6 h-6 hidden lg:block" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-purple-700 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Guests Say</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Hear from travel professionals who have shared their stories on our podcast.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div id="shapo-widget-47f567448d4abc07b8a6"></div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-teal-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Share Your Story?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Join our community of travel voices and inspire thousands of listeners with your unique journey.
            </p>
            <Button 
              size="lg"
              onClick={openBookingPage}
              className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <Mic className="mr-2 h-5 w-5" />
              Book Your Guest Spot
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_speak-on-nifty/artifacts/2oak4kuh_Nifty%20Travel%20Podcast.png"
                  alt="NIFTY Travel Podcast Logo"
                  className="w-10 h-10 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                />
                <h3 className="text-lg font-bold">NIFTY Travel Podcast</h3>
              </div>
              <p className="text-gray-300 mb-4 max-w-md">
                Showcasing travel voices and stories from around the world. Join our community of travel professionals and enthusiasts.
              </p>
              <div className="flex space-x-4">
                <a href={`mailto:${mockData.contact.email}`} className="text-gray-300 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                </a>
                <a href={`https://${mockData.contact.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href={mockData.contact.youtube} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#about" className="hover:text-white transition-colors">About Podcast</a></li>
                <li><a href="#episodes" className="hover:text-white transition-colors">Featured Episodes</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Guest Reviews</a></li>
                <li><button onClick={openBookingPage} className="hover:text-white transition-colors text-left">Book Guest Spot</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Get Started</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={openBookingPage} className="hover:text-white transition-colors text-left">Book a Call</button></li>
                <li><a href={`mailto:${mockData.contact.email}`} className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>

          <Separator className="my-8 bg-gray-700" />
          
          <div className="text-center text-gray-400">
            <p>&copy; 2024 Nifty Travel Podcast. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};